import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Tag, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { formatPriceToman } from '../../../utils/formatPriceToman';
import { financialApi } from '../../../utils/financialApi';
import {
  DollarOutlined,
  WarningOutlined,
  InboxOutlined,
  BankOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  ArrowLeftOutlined,
  WalletOutlined,
  LineChartOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const ReceivablesSummaryWidget = ({ data: initialData, loading: initialLoading = false }) => {
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = useState(initialData || null);
  const [loading, setLoading] = useState(initialLoading);

  useEffect(() => {
    if (initialData) {
      setSummaryData(initialData);
      setLoading(false);
    } else {
      // Fallback: fetch directly if not provided by parent
      setLoading(true);
      financialApi
        .getReceivablesSummary()
        .then((res) => {
          const rec = res?.data?.receivables_summary || res?.receivables_summary || res?.data || res;
          setSummaryData(rec);
        })
        .catch((err) => {
          console.error('Failed to load receivables summary fallback:', err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [initialData]);

  const summary = summaryData || {};
  const chequesCount = summary.cheques_count || {};

  const cards = [
    {
      title: 'کل اقساط در انتظار وصول',
      subtitle: 'اقساط آتی سررسید نشده',
      amount: summary.upcoming_receivables ?? summary.total_installments_pending ?? 0,
      badge: 'آتی / جاری',
      badgeColor: 'blue',
      icon: <CalendarOutlined className="text-blue-500 text-xl" />,
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      valueColor: 'text-blue-600 dark:text-blue-400',
    },
    {
      title: 'مطالبات معوق و در خطر',
      subtitle: `${summary.overdue_installments_count || 0} قسط سررسید گذشته`,
      amount: summary.overdue_receivables ?? summary.total_receivables_at_risk ?? 0,
      badge: `${summary.overdue_installments_count || 0} قسط معوق`,
      badgeColor: 'error',
      icon: <WarningOutlined className="text-amber-500 text-xl" />,
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      valueColor: 'text-amber-600 dark:text-amber-400',
    },
    {
      title: 'چک‌های نزد صندوق',
      subtitle: `${chequesCount.in_vault || 0} فقره چک موجود در گاوصندوق`,
      amount: summary.cheques_in_vault ?? summary.total_cheques_in_vault ?? 0,
      badge: `${chequesCount.in_vault || 0} فقره در صندوق`,
      badgeColor: 'success',
      icon: <InboxOutlined className="text-emerald-500 text-xl" />,
      bg: 'bg-emerald-50 dark:bg-emerald-900/20',
      valueColor: 'text-emerald-600 dark:text-emerald-400',
    },
    {
      title: 'چک‌های در جریان وصول',
      subtitle: `${chequesCount.deposited || 0} فقره واگذار شده به بانک`,
      amount: summary.cheques_deposited ?? 0,
      badge: `${chequesCount.deposited || 0} فقره نزد بانک`,
      badgeColor: 'purple',
      icon: <BankOutlined className="text-purple-500 text-xl" />,
      bg: 'bg-purple-50 dark:bg-purple-900/20',
      valueColor: 'text-purple-600 dark:text-purple-400',
    },
    {
      title: 'چک‌های برگشتی',
      subtitle: `${chequesCount.bounced || 0} فقره برگشت خورده و واخواست شده`,
      amount: summary.cheques_bounced ?? summary.total_cheques_bounced ?? 0,
      badge: `${chequesCount.bounced || 0} فقره برگشتی`,
      badgeColor: 'error',
      icon: <ExclamationCircleOutlined className="text-red-500 text-xl" />,
      bg: 'bg-red-50 dark:bg-red-900/20',
      valueColor: 'text-red-600 dark:text-red-400',
    },
    {
      title: 'جمع کل مطالبات باز',
      subtitle: 'مجموع اقساط معوق و آتی وصول‌نشده',
      amount: summary.total_receivables ?? 0,
      badge: 'مجموع مطالبات',
      badgeColor: 'cyan',
      icon: <DollarOutlined className="text-cyan-600 text-xl" />,
      bg: 'bg-cyan-50 dark:bg-cyan-900/20',
      valueColor: 'text-gray-800 dark:text-gray-100',
    },
  ];

  return (
    <Card
      loading={loading}
      bordered={false}
      className="shadow-sm dark:bg-gray-800 rounded-2xl overflow-hidden"
      bodyStyle={{ padding: '20px 24px' }}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5 pb-4 border-b border-gray-100 dark:border-gray-700">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block animate-pulse"></span>
            <Title level={4} className="m-0 text-gray-800 dark:text-gray-100 font-bold">
              وضعیت وصول مطالبات و اسناد تجاری (چک و اقساط)
            </Title>
          </div>
          <Text className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm mt-1 block">
            خلاصه آمار اقساط در انتظار و معوق، به همراه آخرین وضعیت چک‌های صیادی شرکت
          </Text>
        </div>

        {/* Quick navigation actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="small"
            icon={<WalletOutlined />}
            onClick={() => navigate('/finance/cheques')}
            className="rounded-lg text-xs flex items-center gap-1"
          >
            خزانه چک صیادی
          </Button>
          <Button
            size="small"
            type="primary"
            ghost
            icon={<LineChartOutlined />}
            onClick={() => navigate('/finance/aging-report')}
            className="rounded-lg text-xs flex items-center gap-1"
          >
            گزارش سنی بدهی
            <ArrowLeftOutlined className="text-[10px]" />
          </Button>
        </div>
      </div>

      {/* Grid of stats */}
      <Row gutter={[16, 16]}>
        {cards.map((item, idx) => (
          <Col xs={24} sm={12} md={8} lg={4} key={idx}>
            <div className="p-3.5 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 bg-white dark:bg-gray-800/80 transition-all duration-200 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between mb-2">
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${item.bg}`}>
                  {item.icon}
                </div>
                <Tag color={item.badgeColor} className="m-0 rounded text-[11px]">
                  {item.badge}
                </Tag>
              </div>

              <div>
                <Text className="text-gray-500 dark:text-gray-400 text-xs block mb-1 font-medium truncate" title={item.title}>
                  {item.title}
                </Text>
                <div className={`text-base font-bold ${item.valueColor} tracking-tight`}>
                  {formatPriceToman(item.amount)}
                </div>
                <Text className="text-gray-400 dark:text-gray-500 text-[11px] block mt-1 truncate" title={item.subtitle}>
                  {item.subtitle}
                </Text>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </Card>
  );
};

export default ReceivablesSummaryWidget;
